import { createContext, useState, useMemo } from 'react';
import { getData } from '@portal-internet/bff';
import { useItemsLaMevaLlista } from '../useItemsLaMevaLlista';
import __get from 'lodash/get';
import __isEmpty from 'lodash/isEmpty';

const urlBase = 'https://api.3cat.cat';
export const ContentUserContext = createContext();

const ContentUserProvider = (props) => {
    const { children } = props;
    const [stickyVisible, setStickyVisible] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const RESPOSTA_STATUS = 'resposta.status';
    const RESPOSTA_PAGINACIO = 'resposta.paginacio';

    // Hook compartit
    const {
        itemsLaMevaLlista,
        itemsLaMevaLlistaCloneRef,
        isALaMevaLlista,
        toggleSelection,
        loaded,
        addItemToAPI,
        removeItemToAPI,
        isSpmEnabled,
        isAuthenticated,
        accessToken,
        updateItemsLaMevaLlista
    } = useItemsLaMevaLlista();

    const getFullItemsFromApi = async (pagina, numItems) => {
        if (
            !isAuthenticated ||
            !accessToken ||
            !isSpmEnabled ||
            itemsLaMevaLlista['NOT_NOTICIA'] === undefined ||
            itemsLaMevaLlista['NOT_NOTICIA'].length === 0
        )
            return false;
        setIsLoading(true);

        const urlNot =
            `${urlBase}/usuaris/llistes/obtenircontinguts?_format=json&tipus_llista=permestard&tipologia=NOT_NOTICIA` +
            `&items_pagina=${numItems}&pagina=${pagina}&cache=30&master=yes&usuari_id=${accessToken}`;
        const notRawData = await getData({ queryKey: ['itemsLaMevaLlista', { url: urlNot, fetchFromOrigin: true }] });

        const isStatusNotisOk = __get(notRawData, RESPOSTA_STATUS) === 'OK';
        const items = isStatusNotisOk ? __get(notRawData, 'resposta') : [];

        const idsOrdenats = [...itemsLaMevaLlista['NOT_NOTICIA']].reverse();

        let blocsInfoContingut = Array.isArray(items?.items?.item) ? items.items.item.map((item) => item.info_contingut) : [];

        // Filtra els blocs per mantenir només els que estan a idsOrdenats
        blocsInfoContingut = blocsInfoContingut.filter((bloc) => idsOrdenats.includes(bloc.id));

        // Ordena blocsInfoContingut segons l'ordre de idsOrdenats
        blocsInfoContingut = blocsInfoContingut.sort((a, b) => {
            const idA = a.id; // Assumeix que cada bloc té una propietat 'id'
            const idB = b.id;
            return idsOrdenats.indexOf(idA) - idsOrdenats.indexOf(idB);
        });

        const paginacio = isStatusNotisOk ? __get(notRawData, RESPOSTA_PAGINACIO) : [];
        const moreData = { paginacio, url: urlNot };

        setIsLoading(false);
        return { blocsInfoContingut, moreData };
    };

    const getFullCategoriesInfo = async () => {
        const dtyCategory = Array.isArray(itemsLaMevaLlista['DTY_CATEGORY']) ? itemsLaMevaLlista['DTY_CATEGORY'] : [];
        if (!isAuthenticated || !accessToken || dtyCategory.length === 0) return false;

        setIsLoading(true);

        let idsParamsCategories = '&id=' + Object.values(itemsLaMevaLlista['DTY_CATEGORY']).join(',');

        const urlCategories = `${urlBase}/categories?_format=json&${idsParamsCategories}&debug=no&version=2.0&cache=90`;
        const categoriesRawData = await getData({ queryKey: ['itemsCategories', { url: urlCategories, fetchFromOrigin: true }] });

        const isStatusNotisOk = __get(categoriesRawData, RESPOSTA_STATUS) === 'OK';
        const items = isStatusNotisOk ? __get(categoriesRawData, 'resposta') : [];
        const blocsInfoCategories = Array.isArray(items?.items?.item) ? items?.items.item : [];

        const idsOrdenats = [...itemsLaMevaLlista['DTY_CATEGORY']].reverse();

        const blocsInfoCategoriesOrdenats = idsOrdenats
            .map((id) => blocsInfoCategories.find((categoria) => categoria.idint === id))
            .filter((categoria) => categoria);

        setIsLoading(false);
        return { blocsInfoCategories: blocsInfoCategoriesOrdenats };
    };

    const getFullItemsFromApiDtyCategory = async (tipologia, pagina, numItems, selectedTematicaId, categories, noIds) => {
        const dtyCategory = Array.isArray(itemsLaMevaLlista['DTY_CATEGORY']) ? itemsLaMevaLlista['DTY_CATEGORY'] : [];
        if (!isAuthenticated || !accessToken || __isEmpty(itemsLaMevaLlista) || dtyCategory.length === 0) return false;
        setIsLoading(true);
        const noIdsParam = noIds ? '&no_id=' + Object.values(noIds).join(',') : '';

        let idsParamsCategories = itemsLaMevaLlista?.[tipologia] ? '&tag=' + categories?.map((item) => item.id).join(',') : '';

        if (selectedTematicaId && selectedTematicaId !== 'TOTS') {
            idsParamsCategories = '&tag=' + selectedTematicaId;
        }

        const urlNot =
            `${urlBase}/noticies?_format=json${idsParamsCategories}&no_agrupacio=NOMOSTRAUTOR&origen=llistat&items_pagina=${numItems}` +
            `&pagina=${pagina}&sdom=img&debug=no&version=2.0&cache=90&redl=false&master=yes&perfils_extra=imatges_3catinfo${noIdsParam}`;
        const notRawData = await getData({ queryKey: ['itemsNoticies', { url: urlNot, fetchFromOrigin: true }] });

        const isStatusNotisOk = __get(notRawData, RESPOSTA_STATUS) === 'OK';
        const items = isStatusNotisOk ? __get(notRawData, 'resposta') : [];
        const blocsInfoContingut = Array.isArray(items?.items?.item) ? items?.items.item : [];

        const paginacio = isStatusNotisOk ? __get(notRawData, RESPOSTA_PAGINACIO) : [];
        const moreData = { paginacio, url: urlNot };

        setIsLoading(false);
        return { blocsInfoContingut, moreData };
    };

    const getFullAutorsInfo = async () => {
        const dtyAutor = Array.isArray(itemsLaMevaLlista['NOT_AUTOR']) ? itemsLaMevaLlista['NOT_AUTOR'] : [];
        if (!isAuthenticated || !accessToken || dtyAutor.length === 0) return false;

        setIsLoading(true);

        let idsParamsAutors = '&id=' + Object.values(itemsLaMevaLlista['NOT_AUTOR']).join(',');

        const urlAutors = `${urlBase}/autor?_format=json${idsParamsAutors}&sdom=img&origen=llistat&debug=no&version=2.0&pagina=1&cache=90&redl=false&master=yes`;
        const autorsRawData = await getData({ queryKey: ['itemsAutors', { url: urlAutors, fetchFromOrigin: true }] });

        const isStatusNotisOk = __get(autorsRawData, RESPOSTA_STATUS) === 'OK';
        const items = isStatusNotisOk ? __get(autorsRawData, 'resposta') : [];
        const blocsInfoAutors = Array.isArray(items?.items?.item) ? items?.items.item : [];

        const idsOrdenats = [...itemsLaMevaLlista['NOT_AUTOR']].reverse();

        const blocsInfoAutorsOrdenats = idsOrdenats
            .map((id) => blocsInfoAutors.find((autor) => autor.id === id))
            .filter((autor) => autor);

        setIsLoading(false);
        return { blocsInfoAutors: blocsInfoAutorsOrdenats };
    };

    const getFullItemsFromApiNotAutor = async (tipologia, pagina, numItems, selectedFirmaId, noIds) => {
        const dtyAutor = Array.isArray(itemsLaMevaLlista?.[tipologia]) ? itemsLaMevaLlista[tipologia] : [];

        if (!isAuthenticated || !accessToken || __isEmpty(itemsLaMevaLlista) || dtyAutor.length === 0) return false;
        setIsLoading(true);
        const noIdsParam = noIds ? '&no_id=' + Object.values(noIds).join(',') : '';

        let idsParamsAutors = itemsLaMevaLlista?.[tipologia]
            ? '&autor_id=' + Object.values(itemsLaMevaLlista[tipologia]).join(',')
            : '';

        if (selectedFirmaId && selectedFirmaId !== 'TOTES') {
            idsParamsAutors = '&autor_id=' + selectedFirmaId;
        }

        const urlNot =
            `${urlBase}/noticies?_format=json${idsParamsAutors}&no_agrupacio=NOMOSTRAUTOR&origen=llistat` +
            `&items_pagina=${numItems}&pagina=${pagina}&sdom=img&debug=no&version=2.0&cache=90&redl=false&master=yes&perfils_extra=imatges_3catinfo${noIdsParam}`;
        const notRawData = await getData({ queryKey: ['itemsNoticies', { url: urlNot, fetchFromOrigin: true }] });

        const isStatusNotisOk = __get(notRawData, RESPOSTA_STATUS) === 'OK';
        const items = isStatusNotisOk ? __get(notRawData, 'resposta') : [];
        const blocsInfoContingut = Array.isArray(items?.items?.item) ? items?.items.item : [];

        const paginacio = isStatusNotisOk ? __get(notRawData, RESPOSTA_PAGINACIO) : [];
        const moreData = { paginacio, url: urlNot };

        setIsLoading(false);
        return { blocsInfoContingut, moreData };
    };

    const toggleSelectionNoApi = async (id, tipologia) => {
        const newItems = { ...itemsLaMevaLlista };

        if (!newItems[tipologia]) {
            newItems[tipologia] = [];
        }

        const index = newItems[tipologia].indexOf(id);
        if (index === -1) {
            newItems[tipologia] = [...newItems[tipologia], id];
        } else {
            newItems[tipologia] = newItems[tipologia].filter((itemId) => itemId !== id);
        }

        updateItemsLaMevaLlista(newItems);

        return true;
    };

    const contextValue = useMemo(
        () => ({
            itemsLaMevaLlista,
            isAuthenticated,
            accessToken,
            toggleSelection,
            toggleSelectionNoApi,
            updateItemsLaMevaLlista,
            isALaMevaLlista,
            addItemToAPI,
            removeItemToAPI,
            getFullItemsFromApi,
            getFullItemsFromApiDtyCategory,
            getFullCategoriesInfo,
            getFullItemsFromApiNotAutor,
            getFullAutorsInfo,
            isLoading,
            loaded,
            itemsLaMevaLlistaCloneRef,
            stickyVisible,
            setStickyVisible
        }),
        [
            itemsLaMevaLlista,
            isAuthenticated,
            accessToken,
            toggleSelection,
            toggleSelectionNoApi,
            updateItemsLaMevaLlista,
            isALaMevaLlista,
            addItemToAPI,
            removeItemToAPI,
            getFullItemsFromApi,
            getFullItemsFromApiDtyCategory,
            getFullCategoriesInfo,
            getFullItemsFromApiNotAutor,
            getFullAutorsInfo,
            isLoading,
            loaded,
            itemsLaMevaLlistaCloneRef,
            stickyVisible,
            setStickyVisible
        ]
    );

    return <ContentUserContext.Provider value={contextValue}>{children}</ContentUserContext.Provider>;
};

export default ContentUserProvider;
